<template>
  <div class="waxWeight" ref="scrollable">
    <h1 class="title_text padding page-title">
      Расчет восковки
    </h1>
    <div class="headerBlock-metal padding">
      <SelectMetal v-model="selectedMetal" />
    </div>
    <div class="headerBlock-select padding">
      <div class="switcherBox" style="gap: 12px;" v-if="isOwnDensity">
        Впишите плотность г/см³
        <CastomInput
          :defaultValue="0"
          style="width: 90px;"
          v-model="inputs.density"
          :required="isRequired"
          ref="resetDensity"
        />
      </div>
      <div class="switcherBox" style="gap: 10px;" v-if="!isOwnDensity">
        Выберите пробу
        <CastomSelect
          :placeholder="'Проба'"
          :options="densityOptions"
          style="width: 110px;"
          v-model="inputs.density"
          :required="isRequired"
        />
      </div>
      <div class="switcherBox">
        Своя плотность
        <CastomSwitcher
          id="wax-own-density"
          v-model="isOwnDensity"
        />
      </div>
    </div>
    <div class="inputs padding">
      <div class="inputs-item">
        <div class="inputs-label">Масса восковки г</div>
        <CastomInput
          fontSize="16px"
          v-model="inputs.waxMass"
          ref="resetWaxMass"
          :required="isRequired"
        />
      </div>
      <div class="inputs-item">
        <div class="coefHeader">
          <div>Коэффициент, %</div>
          <CastomSwitcher
            id="wax-coef-enabled"
            v-model="isCoefEnabled"
          />
        </div>
        <div class="coefInput" v-if="isCoefEnabled">
          <CastomInput
            fontSize="16px"
            :defaultValue="0"
            v-model="inputs.coef"
            ref="resetCoef"
          />
        </div>
      </div>
    </div>
    <button
      @click="calculateClick"
      class="button"
    >
      Рассчитать
    </button>

    <div class="footer flexColumn" v-if="metalWeight">
      <div class="footer-container flexRow">
        <div class="footer-text fs14">
          Необходимый вес металла
        </div>
        <div class="footer-number fs14">
          {{ metalWeight }}
        </div>
        <div class="footer-text fs14">
          г
        </div>
      </div>
      <div class="footer-container flexRow" v-if="metalCost">
        <div class="footer-text fs14">
          Стоимость металла
        </div>
        <div class="footer-number fs14">
          {{ formatPrice(metalCost) }}
        </div>
        <div class="footer-text fs14">
          ₽
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue"
import CastomInput from "@/components/inputs/CastomInput"
import CastomSelect from "@/components/selects/CastomSelect"
import CastomSwitcher from "@/components/switchers/CastomSwitcher"
import SelectMetal from "@/components/selects/SelectMetal"
import metalDensity from "@/jsonData/metalDensity.json"
import { useMetalsStore } from "@/stores/metalsPrice/metalsStore"
import { formatPrice } from "@/helpers/helpers"
import { trackCalculation } from '@/helpers/calculationAnalytics'

const selectedMetal = ref('silver')
const isOwnDensity = ref(false)
const isCoefEnabled = ref(false)
const isRequired = ref(false)

const resetWaxMass = ref('')
const resetDensity = ref('')
const resetCoef = ref('')

const scrollable = ref(null)
const metalsStore = useMetalsStore()

const inputs = reactive({
  waxMass: 0, // г
  density: 0, // number | {name,value,density}
  coef: 0, // %
})

const metalWeight = ref(0)

if (window.gtag) {
  window.gtag('event', 'wax-weight_calculator', {})
}

const densityOptions = computed(() => {
  return selectedMetal.value === 'silver' ? metalDensity.silver : metalDensity.gold
})

const densityValue = computed(() => {
  if (isOwnDensity.value) return Number(inputs.density)
  return inputs.density?.density ? Number(inputs.density.density) : 0
})

const assayValue = computed(() => {
  if (isOwnDensity.value) return null
  return inputs.density?.value ? Number(inputs.density.value) : null
})

const metalRate = computed(() => {
  if (!metalsStore?.data) return 0
  return selectedMetal.value === 'gold'
    ? Number(metalsStore.data.gold || 0)
    : Number(metalsStore.data.silver || 0)
})

const metalCost = computed(() => {
  if (isOwnDensity.value) return null
  const w = Number(metalWeight.value) || 0
  const rate = metalRate.value || 0
  const assay = assayValue.value
  if (!w || !rate || !assay) return null
  const cost = w * rate * (assay / 1000)
  return Number.isFinite(cost) ? cost.toFixed(2) : null
})

const waxMassValue = computed(() => Number(inputs.waxMass) || 0)
const coefValue = computed(() => (isCoefEnabled.value ? (Number(inputs.coef) || 0) : 0))

const isValid = computed(() => {
  return waxMassValue.value > 0 && densityValue.value > 0
})

const scrollToBottom = () => {
  if (scrollable.value) {
    scrollable.value.scrollTo({
      top: scrollable.value.scrollHeight,
      behavior: 'smooth',
    })
  }
}

const resetResults = () => {
  metalWeight.value = 0
  if (!inputs.waxMass && resetWaxMass.value?.resetValue) resetWaxMass.value.resetValue()
}

const calculateClick = () => {
  setTimeout(() => {
    scrollToBottom()
  }, 500)

  isRequired.value = true
  if (!isValid.value) {
    metalWeight.value = 0
    return
  }

  const baseWeight = waxMassValue.value * densityValue.value
  const result = baseWeight * (1 + (coefValue.value / 100))
  metalWeight.value = parseFloat(result.toFixed(2))
  trackCalculation('wax_weight')
}

watch(selectedMetal, () => {
  resetResults()
  if (!isOwnDensity.value) {
    inputs.density = 0
  }
})

watch(isOwnDensity, () => {
  inputs.density = 0
  resetResults()
})

watch(isCoefEnabled, (enabled) => {
  if (!enabled) {
    inputs.coef = 0
    if (resetCoef.value?.resetValue) resetCoef.value.resetValue(0)
  }
  resetResults()
})

watch([() => inputs.waxMass, () => inputs.density, () => inputs.coef], () => {
  resetResults()
})
</script>

<style lang="scss" scoped>
.waxWeight {
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  align-items: center;
  text-align: center;
  height: calc(100vh - 140px);
  width: 100%;
  justify-content: flex-start;
  padding-bottom: 10px;
}

.headerBlock {
  &-select {
    margin-top: 30px;
    width: 100%;
    justify-content: space-between;
    display: flex;
    align-items: center;
    padding-right: 20px;
    padding-left: 20px;
  }
  &-metal {
    width: 100%;
  }
}

.switcherBox {
  text-align: center;
  font-size: 14px;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 19px;
  align-items: center;
}

.inputs {
  color: white;
  width: 100%;
  font-size: 16px;
  padding: 15px 0 5px 0 !important;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  &-item{
    width: 100%;
    display: grid;
    grid-template-rows: 34px auto;
    align-items: center;
    gap: 8px;
  }
}

.inputs-label {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-size: 14px;
}

.coefHeader {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 14px;
  width: 100%;
  height: 34px;
}

.coefInput {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &-label {
    font-size: 14px;
    opacity: 0.85;
    min-width: 12px;
    text-align: right;
  }
}

@media (max-width: 420px) {
  .inputs {
    grid-template-columns: 1fr;
  }
  .headerBlock-select {
    flex-direction: column;
    gap: 14px;
  }
}

.footer {
  margin-top: 20px;
  margin-bottom: 20px;
  border-radius: 25px;
  border: 2px solid #fff;
  height: auto;
  padding: 10px;
  width: 90%;
  box-shadow: 0 2px 2px 2px rgba(0, 0, 0, 0.25);
  background: linear-gradient(180deg, rgba(181, 162, 189, 0.33) 0%, rgba(26, 21, 28, 0.33) 100%);
  gap: 7px;

  &-container {
    color: white;
    justify-content: flex-start
  }
  &-text {
    font-size: 18px;
    font-weight: bold;
    margin-left: 10px;
  }
  &-number {
    font-size: 18px;
    font-weight: bold;
    margin-left: auto;
  }
}

.fs14 {
  font-size: 14px;
}

.padding {
  border: 2px solid white;
  border-left: none;
  border-right: none;
  border-bottom: none;
  padding: 10px 0;
}

.flexColumn {
  display: flex;
  flex-direction: column;
}

.flexRow {
  display: flex;
  flex-direction: row;
}

.title_text {
  color: white;
  width: 100%;
}

.button {
  background: rgba(0, 0, 0, 0.22);
  color: rgba(255, 255, 255, 0.98);
  width: 100%;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.2px;
  margin-top: 10px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  cursor: pointer;
  padding: 14px 20px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: transform 0.08s ease, filter 0.18s ease;
  user-select: none;
}

.button:active {
  transform: translateY(1px) scale(0.99);
  filter: brightness(0.96);
}

.button:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.95);
  outline-offset: 2px;
}
</style>

